/**
 * MEVIA v5.0 - Strategic Plans Hierarchy View
 * 
 * Displays the hierarchical structure of strategic plans:
 * - Level 0: Strategic Objectives
 * - Level 1: Enterprise Plan
 * - Level 2: Development Areas (by quarter)
 * - Level 3: Tasks
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Checkbox } from './ui/checkbox';
import {
  ChevronDown, ChevronRight, Target, Calendar, ListTodo, CheckCircle2,
  Clock, AlertCircle, Loader2, RefreshCw, Plus, BarChart3, TrendingUp,
  Play, Pause
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import logger from '../utils/logger';


// Translations
const TEXTS = {
  es: {
    title: 'Plan Estratégico',
    subtitle: 'Jerarquía de objetivos, áreas y tareas',
    noPlan: 'No hay plan empresarial activo',
    generatePlan: 'Generar Plan',
    publishPlan: 'Publicar Plan',
    refresh: 'Actualizar',
    progress: 'Progreso',
    quarter: 'Trimestre',
    tasks: 'Tareas',
    areas: 'Áreas',
    objectives: 'Objetivos Estratégicos',
    
    // Status
    draft: 'Borrador',
    active: 'Activo',
    completed: 'Completado',
    pending: 'Pendiente',
    inProgress: 'En Progreso',
    
    // Priority
    high: 'Alta',
    medium: 'Media',
    low: 'Baja',
    
    // Actions
    markComplete: 'Marcar como completada',
    updateProgress: 'Actualizar progreso',
    viewDetails: 'Ver detalles',
    
    // Messages
    planGenerated: 'Plan generado exitosamente',
    planPublished: 'Plan publicado exitosamente',
    taskUpdated: 'Tarea actualizada',
    noGaps: 'No hay brechas significativas para generar un plan',
    
    // Quarters
    q1: 'T1 - Ene-Mar',
    q2: 'T2 - Abr-Jun',
    q3: 'T3 - Jul-Sep',
    q4: 'T4 - Oct-Dic',
    
    // Section titles
    progressByQuarter: 'Progreso por Trimestre',

    // Capitals
    FISICO: 'Capital Fisico',
    HUMANO: 'Capital Humano',
    MANUFACTURADO: 'Capital Manufacturado',
    FINANCIERO: 'Capital Financiero',
    NATURAL: 'Capital Natural',
    CULTURAL: 'Capital Cultural',
    SOCIAL: 'Capital Social',
    CONOCIMIENTO: 'Conocimiento',
    PSICOLOGICO: 'Capital Psicologico',
    PROPOSITO: 'Proposito'
  },
  en: {
    title: 'Strategic Plan',
    subtitle: 'Hierarchy of objectives, areas and tasks',
    noPlan: 'No active enterprise plan',
    generatePlan: 'Generate Plan',
    publishPlan: 'Publish Plan',
    refresh: 'Refresh',
    progress: 'Progress',
    quarter: 'Quarter',
    tasks: 'Tasks',
    areas: 'Areas',
    objectives: 'Strategic Objectives',
    
    // Status
    draft: 'Draft',
    active: 'Active',
    completed: 'Completed',
    pending: 'Pending',
    inProgress: 'In Progress',
    
    // Priority
    high: 'High',
    medium: 'Medium',
    low: 'Low',
    
    // Actions
    markComplete: 'Mark as complete',
    updateProgress: 'Update progress',
    viewDetails: 'View details',
    
    // Messages
    planGenerated: 'Plan generated successfully',
    planPublished: 'Plan published successfully',
    taskUpdated: 'Task updated',
    noGaps: 'No significant gaps to generate a plan',
    
    // Quarters
    q1: 'Q1 - Jan-Mar',
    q2: 'Q2 - Apr-Jun',
    q3: 'Q3 - Jul-Sep',
    q4: 'Q4 - Oct-Dec',
    
    // Section titles
    progressByQuarter: 'Progress by Quarter',

    // Capitals
    FISICO: 'Physical Capital',
    HUMANO: 'Human Capital',
    MANUFACTURADO: 'Manufactured Capital',
    FINANCIERO: 'Financial Capital',
    NATURAL: 'Natural Capital',
    CULTURAL: 'Cultural Capital',
    SOCIAL: 'Social Capital',
    CONOCIMIENTO: 'Knowledge',
    PSICOLOGICO: 'Psychological Capital',
    PROPOSITO: 'Purpose'
  }
};

const PRIORITY_COLORS = {
  ALTA: 'bg-red-100 text-red-800 border-red-300',
  MEDIA: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  BAJA: 'bg-green-100 text-green-800 border-green-300'
};

const STATUS_COLORS = {
  BORRADOR: 'bg-gray-100 text-gray-800',
  ACTIVO: 'bg-blue-100 text-blue-800',
  COMPLETADO: 'bg-green-100 text-green-800',
  PENDIENTE: 'bg-gray-100 text-gray-800',
  EN_PROGRESO: 'bg-yellow-100 text-yellow-800'
};

// Task Item Component
const TaskItem = ({ task, onUpdate, t }) => {
  const [updating, setUpdating] = useState(false);
  
  const handleToggleComplete = async () => {
    setUpdating(true);
    try {
      const newStatus = task.estatus === 'COMPLETADO' ? 'PENDIENTE' : 'COMPLETADO';
      const newProgress = newStatus === 'COMPLETADO' ? 100 : 0;
      
      await axios.patch(`/v5/estrategia/tareas/${task.tarea_id}`, {
        estatus: newStatus,
        progreso_pct: newProgress
      });
      
      onUpdate();
      toast.success(t.taskUpdated);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    } finally {
      setUpdating(false);
    }
  };
  
  return (
    <div className="flex items-center gap-3 py-2 px-3 hover:bg-gray-50 rounded-lg group">
      <Checkbox
        checked={task.estatus === 'COMPLETADO'}
        onCheckedChange={handleToggleComplete}
        disabled={updating}
        className="data-[state=checked]:bg-green-500"
      />
      <div className="flex-1">
        <p className={`text-sm ${task.estatus === 'COMPLETADO' ? 'line-through text-gray-400' : ''}`}>
          {task.titulo}
        </p>
        {task.descripcion && (
          <p className="text-xs text-gray-500">{task.descripcion}</p>
        )}
      </div>
      {task.fecha_limite && (
        <span className="text-xs text-gray-400 flex items-center gap-1">
          <Clock className="w-3 h-3" />
          {new Date(task.fecha_limite).toLocaleDateString()}
        </span>
      )}
      {updating && <Loader2 className="w-4 h-4 animate-spin" />}
    </div>
  );
};

// Development Area Component
const DevelopmentArea = ({ area, onUpdate, t }) => {
  const [expanded, setExpanded] = useState(false);
  
  const brecha = area.brecha_origen || {};
  const capital = brecha.capital;
  
  return (
    <div className="border rounded-lg overflow-hidden">
      <div 
        className="flex items-center gap-3 p-4 cursor-pointer hover:bg-gray-50"
        onClick={() => setExpanded(!expanded)}
      >
        {expanded ? (
          <ChevronDown className="w-5 h-5 text-gray-400" />
        ) : (
          <ChevronRight className="w-5 h-5 text-gray-400" />
        )}
        
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h4 className="font-medium">{area.titulo}</h4>
            <Badge className={PRIORITY_COLORS[area.prioridad] || PRIORITY_COLORS.MEDIA}>
              {t[area.prioridad?.toLowerCase()] || area.prioridad}
            </Badge>
          </div>
          {capital && (
            <p className="text-sm text-gray-500">
              {t[capital]} • Delta: +{brecha.delta} pts
            </p>
          )}
        </div>
        
        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className="text-sm font-medium">{area.progreso_pct}%</div>
            <div className="text-xs text-gray-500">{area.tasks?.length || 0} {t.tasks}</div>
          </div>
          <div className="w-24">
            <Progress value={area.progreso_pct} className="h-2" />
          </div>
        </div>
      </div>
      
      {expanded && area.tasks && (
        <div className="border-t bg-gray-50/50 p-3">
          {area.tasks.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-2">Sin tareas</p>
          ) : (
            area.tasks.map((task) => (
              <TaskItem key={task.tarea_id} task={task} onUpdate={onUpdate} t={t} />
            ))
          )}
        </div>
      )}
    </div>
  );
};

// Quarter Section Component
const QuarterSection = ({ quarter, areas, onUpdate, t }) => {
  const quarterLabels = { 1: t.q1, 2: t.q2, 3: t.q3, 4: t.q4 };
  
  if (areas.length === 0) return null;
  
  const totalProgress = areas.reduce((sum, a) => sum + (a.progreso_pct || 0), 0) / areas.length;
  
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-blue-600" />
          <h3 className="font-semibold">{quarterLabels[quarter] || `Q${quarter}`}</h3>
          <Badge variant="outline">{areas.length} {t.areas}</Badge>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">{Math.round(totalProgress)}%</span>
          <Progress value={totalProgress} className="w-24 h-2" />
        </div>
      </div>
      
      <div className="space-y-2 pl-7">
        {areas.map((area) => (
          <DevelopmentArea key={area.area_id} area={area} onUpdate={onUpdate} t={t} />
        ))}
      </div>
    </div>
  );
};

// Main Component
export default function StrategicPlanView({ empresaId }) {
  const { language } = useLanguage();
  const { user } = useAuth();
  const t = TEXTS[language] || TEXTS.es;
  
  const effectiveEmpresaId = empresaId || user?.empresa_id;
  
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [planData, setPlanData] = useState(null);
  const [progressData, setProgressData] = useState(null);

  const loadPlan = useCallback(async () => {
    if (!effectiveEmpresaId) return;
    
    try {
      setLoading(true);
      
      const [planRes, progressRes] = await Promise.all([
        axios.get(`/v5/estrategia/empresa/${effectiveEmpresaId}/plan`),
        axios.get(`/v5/estrategia/empresa/${effectiveEmpresaId}/progreso`)
      ]);
      
      if (planRes.data.success) {
        setPlanData(planRes.data.data);
      }
      
      if (progressRes.data.success) {
        setProgressData(progressRes.data.data);
      }
    } catch (error) {
      logger.error('Error loading plan:', error);
    } finally {
      setLoading(false);
    }
  }, [effectiveEmpresaId]);

  useEffect(() => {
    loadPlan();
  }, [loadPlan]);

  const handleGeneratePlan = async () => {
    try {
      setGenerating(true);
      const response = await axios.post(
        `/v5/estrategia/empresa/${effectiveEmpresaId}/plan/generar?lang=${language}`
      );
      
      if (response.data.success) {
        if (response.data.data.plan_generated) {
          toast.success(t.planGenerated);
          loadPlan();
        } else {
          toast.info(t.noGaps);
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    } finally {
      setGenerating(false);
    }
  };

  const handlePublishPlan = async () => {
    if (!planData?.plan?.plan_id) return;
    
    try {
      setPublishing(true);
      await axios.post(
        `/v5/estrategia/empresa/${effectiveEmpresaId}/plan/${planData.plan.plan_id}/publicar`
      );
      
      toast.success(t.planPublished);
      loadPlan();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    } finally {
      setPublishing(false);
    }
  };

  // Group areas by quarter
  const areasByQuarter = planData?.areas?.reduce((acc, area) => {
    const q = area.trimestre_asignado || 1;
    if (!acc[q]) acc[q] = [];
    acc[q].push(area);
    return acc;
  }, {}) || {};

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </CardContent>
      </Card>
    );
  }

  // No plan state
  if (!planData?.has_plan) {
    return (
      <Card>
        <CardContent className="text-center py-12">
          <Target className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">{t.noPlan}</h3>
          <p className="text-gray-500 mb-6">
            {language === 'es' 
              ? 'Genera un plan basado en las brechas de tu perfil objetivo'
              : 'Generate a plan based on your objective profile gaps'}
          </p>
          <Button onClick={handleGeneratePlan} disabled={generating}>
            {generating ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Plus className="w-4 h-4 mr-2" />
            )}
            {t.generatePlan}
          </Button>
        </CardContent>
      </Card>
    );
  }

  const plan = planData.plan;

  return (
    <div className="space-y-6" data-testid="strategic-plan-view">
      {/* Header Card */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Target className="w-6 h-6 text-blue-600" />
                {plan.titulo}
              </CardTitle>
              <CardDescription>{plan.descripcion}</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Badge className={STATUS_COLORS[plan.estado] || STATUS_COLORS.BORRADOR}>
                {plan.estado === 'BORRADOR' ? t.draft : plan.estado === 'ACTIVO' ? t.active : plan.estado}
              </Badge>
              
              {plan.estado === 'BORRADOR' && (
                <Button size="sm" onClick={handlePublishPlan} disabled={publishing}>
                  {publishing ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Play className="w-4 h-4 mr-2" />
                  )}
                  {t.publishPlan}
                </Button>
              )}
              
              <Button variant="outline" size="sm" onClick={loadPlan}>
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        
        <CardContent>
          {/* Progress Overview */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="p-4 bg-blue-50 rounded-lg text-center">
              <div className="text-3xl font-bold text-blue-600">
                {progressData?.overall?.progreso_pct || plan.progreso_global_pct || 0}%
              </div>
              <div className="text-sm text-gray-600">{t.progress}</div>
            </div>
            
            <div className="p-4 bg-gray-50 rounded-lg text-center">
              <div className="text-3xl font-bold text-gray-700">
                {planData.total_areas || 0}
              </div>
              <div className="text-sm text-gray-600">{t.areas}</div>
            </div>
            
            <div className="p-4 bg-green-50 rounded-lg text-center">
              <div className="text-3xl font-bold text-green-600">
                {progressData?.overall?.completadas || 0}
              </div>
              <div className="text-sm text-gray-600">{t.completed}</div>
            </div>
            
            <div className="p-4 bg-yellow-50 rounded-lg text-center">
              <div className="text-3xl font-bold text-yellow-600">
                {planData.areas?.reduce((sum, a) => sum + (a.tasks?.length || 0), 0) || 0}
              </div>
              <div className="text-sm text-gray-600">{t.tasks}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Progress by Quarter Chart */}
      {progressData?.by_quarter && progressData.by_quarter.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-purple-600" />
              {t.progressByQuarter}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((q) => {
                const quarterData = progressData.by_quarter.find(qd => qd.trimestre === q);
                const progress = quarterData?.progreso_pct || 0;
                const total = quarterData?.total || 0;
                const completed = quarterData?.completadas || 0;
                
                return (
                  <div key={q} className="text-center">
                    <div className="relative h-24 bg-gray-100 rounded-lg overflow-hidden mb-2">
                      <div 
                        className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-blue-600 to-blue-400 transition-all duration-500"
                        style={{ height: `${progress}%` }}
                      />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="font-bold text-lg">{Math.round(progress)}%</span>
                      </div>
                    </div>
                    <div className="text-sm font-medium">T{q}</div>
                    <div className="text-xs text-gray-500">{completed}/{total}</div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Areas by Quarter */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <ListTodo className="w-5 h-5 text-orange-600" />
            {t.areas} de Desarrollo
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {[1, 2, 3, 4].map((quarter) => (
            <QuarterSection
              key={quarter}
              quarter={quarter}
              areas={areasByQuarter[quarter] || []}
              onUpdate={loadPlan}
              t={t}
            />
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
